import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { concatLatestFrom } from '@ngrx/operators';
import { Observable, combineLatest, forkJoin, of, zip } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, take, tap } from 'rxjs/operators';

import {
  EquipmentApi,
  EquipmentApiPicker,
  Feature,
  FeatureNames,
  IsEnabledPropertyGeneric,
  RemoteServiceApi,
  RemoteServiceDiagnosisQueryParams,
  RemoteServiceDiagnosisResponse,
  SerialEditorType,
} from '@vi-iot/client';

import {
  AuthStore,
  DevicesStore,
  GatewayMainDeviceDetails,
  InstallationFeaturesStore,
  RichDevice,
} from '@store';
import { InstallationsService } from '../../+installations/services/installations.service';

import { ActionMessagesService } from '../_services/action-messages.service';
import { InstallationDetailsStore } from '../installation-details/installation-details.store';

import { InstallationDetailsMappers } from '../installation-details/installation-details.mappers';
import { DevicesActions } from './devices.actions';
import dayjs from 'dayjs';
import { RemoteServiceMapper } from '../remote-service/remote-service.mapper';
import { TrendingCategory } from 'src/app/+installations/installation/trending/trending-chart/trending-chart.helpers';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class DevicesEffects {
  updateDevices$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(DevicesActions.updateDevices),
      concatLatestFrom(() => [this.installationDetailsStore.getInstallationId$]),
      switchMap(([_action, installationId]) =>
        this.equipmentApi.getInstallation(installationId).pipe(
          map((response) => InstallationDetailsMappers.mapAllDevices(response.data)),
          map((devices) => InstallationDetailsMappers.mapToDevicesWithFeatures(devices)),
          map((devices) => DevicesActions.updateDevicesSuccess({ devices }))
        )
      )
    );
  });

  updateBmuSerial$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(DevicesActions.updateBmuSerial),
      mergeMap((action) =>
        combineLatest([
          of(action),
          this.installationDetailsStore.getInstallationId$.pipe(take(1)),
          this.installationDetailsStore.gatewaySerial$.pipe(take(1)),
          this.devicesStore.selectedDevice$.pipe(take(1)),
        ])
      ),
      switchMap(([action, installationId, gatewaySerial, deviceDetails]) =>
        this.equipmentApi
          .updateBmuSerial(installationId, gatewaySerial, deviceDetails?.id, action.bmuSerial)
          .pipe(
            map(() => {
              this.actionMessagesService.onBmuSerialSuccess();

              return DevicesActions.updateBmuSerialSuccess({
                installationId,
                bmuSerial: action.bmuSerial,
              });
            }),
            catchError((err) => {
              this.actionMessagesService.onBmuSerialError();

              return of(DevicesActions.updateBmuSerialError({ error: err?.error }));
            })
          )
      )
    );
  });

  updateBoilerSerial$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(DevicesActions.updateBoilerSerial),
      mergeMap((action) =>
        zip(
          of(action),
          this.installationDetailsStore.getInstallationId$,
          this.installationDetailsStore.gatewaySerial$,
          this.devicesStore.selectedDevice$
        ).pipe(take(1))
      ),
      switchMap(([action, installationId, gatewaySerial, deviceDetails]) =>
        this.equipmentApi
          .updateDevice(installationId, gatewaySerial, deviceDetails?.id, action.boilerSerial)
          .pipe(
            map(() => {
              this.actionMessagesService.onSerialSuccess(installationId);

              return DevicesActions.updateBoilerSerialSuccess({
                installationId,
                boilerSerial: action.boilerSerial,
              });
            }),
            catchError(() => {
              this.actionMessagesService.onSerialError(installationId);

              return of(DevicesActions.updateBoilerSerialError());
            })
          )
      )
    );
  });

  updateBoilerSerialSuccess$: Observable<Action> = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(DevicesActions.updateBoilerSerialSuccess),
        tap(() => this.installationService.listInstallations())
      );
    },
    { dispatch: false }
  );

  triggerBoilerMaterialDataRead$: Observable<void> = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(DevicesActions.loadDevicesSuccess),
        concatLatestFrom(() => this.devicesStore.mainAndLagDevices$),
        map(([{}, mainAndLagDevices]) => {
          mainAndLagDevices.forEach((device: RichDevice) => {
            if (device.boilerSerial) {
              this.store.dispatch(
                DevicesActions.loadMaterialDataWhenSerialIsPresent({
                  serialNumber: device.boilerSerial,
                  device,
                })
              );
            } else {
              this.store.dispatch(
                DevicesActions.loadMaterialDataWhenSerialIsNotPresent({
                  device,
                })
              );
            }
          });
        })
      );
    },
    { dispatch: false }
  );

  readMaterialData$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(DevicesActions.loadMaterialDataWhenSerialIsPresent),
      switchMap((action) =>
        combineLatest([of(action), this.getPrimaryVkorgBasedOnCompanyId().pipe(take(1))])
      ),
      mergeMap(([action, vkorg]) =>
        forkJoin([
          this.equipmentApiPicker
            .instance()
            .getMaterialDetails(action.serialNumber)
            .pipe(catchError(() => of(null))),
          this.equipmentApi
            .getMaintenancePackages(action.serialNumber, vkorg)
            .pipe(catchError(() => of(null))),
          this.equipmentApiPicker
            .instance()
            .equipmentSummary(action.serialNumber)
            .pipe(catchError(() => of(null))),
          this.equipmentApiPicker
            .instance()
            .getMaterialGatewayName(action.device.gatewaySerial)
            .pipe(catchError(() => of(null))),
        ]).pipe(
          map(([materialDetails, maintenancePackage, equipmentSummary, gatewayMaterialName]) => {
            const mainDevicesDetails: GatewayMainDeviceDetails = {
              id: action.device.id,
              boilerSerial: action.device.boilerSerial,
              gatewaySerial: action.device.gatewaySerial,
              gatewayType:
                gatewayMaterialName?.materialDetails[0]?.description ||
                gatewayMaterialName?.materialDetails[0]?.name,
              marketingName: materialDetails?.materialDetails[0]?.description,
              deviceImage: equipmentSummary.materialPictogram || equipmentSummary.materialImage,
              shortDeviceName: equipmentSummary?.name || materialDetails?.materialDetails[0]?.name,
              canEditBoilerSerial:
                action.device?.boilerSerialEditor !== SerialEditorType.DeviceCommunication,
              maintenancePackageLink: maintenancePackage?.link,
              equipmentNumber: equipmentSummary?.equipmentNumber,
              error: null,
              loading: false,
            };

            return DevicesActions.loadBoilerSerialDataSuccess({ mainDevicesDetails });
          }),
          catchError((error) =>
            of(
              DevicesActions.loadBoilerSerialDataError({
                error: error?.error,
              })
            )
          )
        )
      )
    );
  });

  readMaterialDataWhenSerialIsNotPresent$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(DevicesActions.loadMaterialDataWhenSerialIsNotPresent),
      mergeMap((action) =>
        combineLatest([
          of(action),
          this.equipmentApiPicker
            .instance()
            .getMaterialGatewayName(action.device.gatewaySerial)
            .pipe(catchError(() => of(null))),
        ])
      ),
      map(([action, gatewayMaterialName]) => {
        const mainDevicesDetails: GatewayMainDeviceDetails = {
          id: action.device?.id,
          boilerSerial: null,
          gatewaySerial: action.device?.gatewaySerial,
          gatewayType:
            gatewayMaterialName?.materialDetails[0]?.description ||
            gatewayMaterialName?.materialDetails[0]?.name,
          marketingName: '',
          deviceImage: '',
          shortDeviceName: '',
          canEditBoilerSerial:
            action.device?.boilerSerialEditor !== SerialEditorType.DeviceCommunication,
          maintenancePackageLink: '',
          equipmentNumber: '',
          error: null,
          loading: false,
        };

        return DevicesActions.loadBoilerSerialDataSuccess({ mainDevicesDetails });
      }),
      catchError((error) => {
        return of(
          DevicesActions.loadBoilerSerialDataError({
            error: error?.error,
          })
        );
      })
    );
  });

  getDiagnosticDataWithListMainDevices$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(DevicesActions.loadDiagnosticDataByDevices.attempt),

      concatLatestFrom(() => [
        this.devicesStore.mainAndLagDevices$,
        this.authStore.isRsaUser(),
        this.authStore.isMaintainer$,
      ]),
      switchMap(([_action, mainAndLagDevices, isRsa, isMaintainer]) => {
        const observableDiagnosticDataArray: Observable<{
          success: boolean;
          data: RemoteServiceDiagnosisResponse;
        }>[] = [];
        mainAndLagDevices.forEach((device) => {
          const params: RemoteServiceDiagnosisQueryParams = {
            boiler_id: device.boilerSerial,
            start_datetime: dayjs().subtract(2, 'days').endOf('day').toISOString(),
            end_datetime: dayjs().startOf('day').toISOString(),
          };

          if (params.boiler_id) {
            const request = this.remoteServiceApi.getDiagnosis(params).pipe(
              map((diagnosis: RemoteServiceDiagnosisResponse) => {
                const diagnosisData = RemoteServiceMapper.normalizeDiagnosisData(diagnosis, {
                  isRsa,
                  isMaintainer,
                });

                const updateResponse: RemoteServiceDiagnosisResponse = {
                  data: {
                    ...diagnosis.data,
                    data: diagnosisData,
                  },
                };

                return {
                  success: true,
                  data: updateResponse,
                };
              }),
              catchError(() => {
                return of({
                  success: false,
                  data: {} as RemoteServiceDiagnosisResponse,
                });
              })
            );

            observableDiagnosticDataArray.push(request);
          }
        });

        return forkJoin(observableDiagnosticDataArray);
      }),
      map((responseDiagnosticDataArray) => {
        const isAllSubscriptionsFailed = responseDiagnosticDataArray.every(
          (subscription) => !subscription?.success
        );
        if (isAllSubscriptionsFailed) {
          return DevicesActions.loadDiagnosticDataByDevices.error();
        }

        const diagnosticDataArray = responseDiagnosticDataArray.map((item) => {
          return item.data;
        });

        return DevicesActions.loadDiagnosticDataByDevices.success({ diagnosticDataArray });
      })
    );
  });

  getBatteryModuleCategories$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(DevicesActions.loadBatteryModuleCategories.attempt),
      concatLatestFrom(() => this.devicesStore.selectVitoChargeDevices$),
      switchMap(([_action, vitoChargeDevices]) => {
        const batteryArray: Observable<
          Feature<IsEnabledPropertyGeneric<number>, never>
        >[] = vitoChargeDevices.map((device) =>
          this.installationFeaturesStore
            .selectInstallationDeviceFeature<IsEnabledPropertyGeneric<number>, never>(
              FeatureNames.batteries,
              device.id
            )
            .pipe(
              catchError((_err) => {
                return of(null);
              })
            )
        );

        return combineLatest(batteryArray).pipe(
          map((responseBatteryArray) => {
            const batteryModuleCategories: TrendingCategory[] = responseBatteryArray
              .filter((item) => item)
              .map((item) => {
                return {
                  deviceId: item.deviceId,
                  items: item.properties.enabled.value.map((value, index) => {
                    return {
                      categoryId: value,
                      categoryName: this.translateService.instant(
                        '+INSTALLATIONS.INSTALLATION.TRENDING.FEATURE.ENERGY_STORAGE.MODULE_CATEGORY',
                        { index: index + 1 }
                      ),
                      features: [],
                      active: false,
                    };
                  }),
                };
              });

            if (batteryModuleCategories.length === 0) {
              return DevicesActions.loadBatteryModuleCategories.error();
            }

            return DevicesActions.loadBatteryModuleCategories.success({ batteryModuleCategories });
          })
        );
      })
    );
  });
  private getPrimaryVkorgBasedOnCompanyId(): Observable<string> {
    const defaultValue = '';

    return this.authStore.isMaintainer$.pipe(
      switchMap((isMaintainer) => {
        if (isMaintainer) {
          return this.authStore.primaryVkorg$.pipe(filter((vkorg) => !!vkorg));
        }

        return of(defaultValue);
      })
    );
  }

  constructor(
    private actions$: Actions,
    private store: Store,
    private installationDetailsStore: InstallationDetailsStore,
    private devicesStore: DevicesStore,
    private equipmentApi: EquipmentApi,
    private actionMessagesService: ActionMessagesService,
    private installationService: InstallationsService,
    private authStore: AuthStore,
    private equipmentApiPicker: EquipmentApiPicker,
    private remoteServiceApi: RemoteServiceApi,
    private installationFeaturesStore: InstallationFeaturesStore,
    private translateService: TranslateService
  ) {}
}
